import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Plus, TrendingUp, Users, DollarSign, Clock, LogOut, CheckCircle, Gift, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import useAmbassador from "@/hooks/useAmbassador";
import useAuth from "@/hooks/useAuth";
import Spinner from "@/components/Spinner";

const Ambassador = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const { ambassador, referralCodes, referrals, stats, loading, isAmbassador, generateReferralCode } = useAmbassador();
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    const newCode = await generateReferralCode();
    setGeneratingCode(false);

    if (newCode) {
      toast({
        title: "Code Generated",
        description: `Your new referral code is: ${newCode.code}`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to generate referral code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (code: string) => {
    const referralLink = `https://cozystamp.com/?ref=${code}`;
    await navigator.clipboard.writeText(referralLink);
    setCopiedCode(code);
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard",
    });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size={32} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Ambassador Portal</CardTitle>
            <CardDescription>Please sign in to access your dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/auth")} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAmbassador) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have ambassador access. Contact an administrator if you believe this is an error.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Signed in as: {user.email}
            </p>
            <Button onClick={handleSignOut} variant="outline" className="w-full">
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Ambassador Dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome back, {ambassador?.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalReferrals}</div>
              <p className="text-xs text-muted-foreground">Users signed up with your codes</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{stats.totalEarnings.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Lifetime referral commission</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Paid Out</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{stats.paidOut.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Commission already paid</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{stats.pending.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Awaiting payout</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Prize Draw Entries</CardTitle>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalPrizeEntries}</div>
              <p className="text-xs text-muted-foreground">Total entries via your link</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Prize Commission</CardTitle>
              <Coins className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{stats.prizeCommission.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Your 30% share from prize draws</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Codes Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Your Referral Codes</CardTitle>
              <CardDescription>Share these codes with potential users</CardDescription>
            </div>
            <Button onClick={handleGenerateCode} disabled={generatingCode}>
              {generatingCode ? (
                <Spinner size={16} />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Code
                </>
              )}
            </Button>
          </CardHeader>
          <CardContent>
            {referralCodes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No referral codes yet. Generate your first code to get started!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referralCodes.map((code) => (
                  <div
                    key={code.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30"
                  >
                    <div className="flex-1">
                      <p className="font-mono text-lg font-bold">{code.code}</p>
                      <p className="text-sm text-muted-foreground">
                        {code.uses_count} uses • Created {new Date(code.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(code.code)}
                    >
                      {copiedCode === code.code ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Referrals</CardTitle>
            <CardDescription>Users who signed up with your codes</CardDescription>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No referrals yet. Share your codes to start earning!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.slice(0, 10).map((referral) => (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border"
                  >
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString()} at{" "}
                        {new Date(referral.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">£{Number(referral.commission_amount).toFixed(2)}</p>
                      <p className={`text-xs ${referral.paid_out ? "text-green-500" : "text-yellow-500"}`}>
                        {referral.paid_out ? "Paid" : "Pending"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commission Info */}
        <Card>
          <CardHeader>
            <CardTitle>Commission Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-primary">
              £{Number(ambassador?.commission_rate || 5).toFixed(2)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              You earn this amount for each user who signs up with your referral code
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Ambassador;
